import { useEffect } from "react";
import { BrowserRouter, Routes, Route, useLocation, Navigate } from "react-router-dom";
import { Toaster } from "sonner";
import "@/App.css";
import { AuthProvider, useAuth } from "@/contexts/AuthContext";
import Layout from "@/components/Layout";
import Login from "@/pages/Login";
import AuthCallback from "@/pages/AuthCallback";
import Landing from "@/pages/Landing";
import Dashboard from "@/pages/Dashboard";
import Programs from "@/pages/Programs";
import ProgramDetail from "@/pages/ProgramDetail";
import Agencies from "@/pages/Agencies";
import Contacts from "@/pages/Contacts";
import Properties from "@/pages/Properties";
import PropertyDetail from "@/pages/PropertyDetail";
import MatchingEngine from "@/pages/MatchingEngine";
import Underwriting from "@/pages/Underwriting";
import Packets from "@/pages/Packets";
import PacketDetail from "@/pages/PacketDetail";
import Funding from "@/pages/Funding";
import Scenarios from "@/pages/Scenarios";
import AccessRequests from "@/pages/AccessRequests";
import ForgotPassword from "@/pages/ForgotPassword";
import ResetPassword from "@/pages/ResetPassword";
import Leads from "@/pages/Leads";
import LeadProfile from "@/pages/LeadProfile";
import AdminDataInventory from "@/pages/AdminDataInventory";
import AdminAlternativeUses from "@/pages/AdminAlternativeUses";
import Seeker from "@/pages/Seeker";
import Provider from "@/pages/Provider";
import Place from "@/pages/Place";
import PublicPacket from "@/pages/PublicPacket";
import PacketPreview from "@/pages/PacketPreview";
import { Privacy, Terms, Accessibility } from "@/pages/PolicyPages";
import InviteSetup from "@/pages/InviteSetup";
import VerifyLead from "@/pages/VerifyLead";

function Protected({ children }) {
  const { user, loading } = useAuth();
  if (loading) return <div className="min-h-screen flex items-center justify-center text-sm text-slate-500">Loading…</div>;
  if (!user) return <Navigate to="/login" replace />;
  return <Layout>{children}</Layout>;
}

function AppRouter() {
  const location = useLocation();
  if (location.hash?.includes("session_id=")) return <AuthCallback />;
  return (
    <Routes>
      <Route path="/share/packet/:token" element={<PublicPacket />} />
      <Route path="/invite/:token" element={<InviteSetup />} />
      <Route path="/verify-lead/:token" element={<VerifyLead />} />
      <Route path="/login" element={<Login />} />
      <Route path="/forgot-password" element={<ForgotPassword />} />
      <Route path="/reset-password/:token" element={<ResetPassword />} />
      <Route path="/" element={<Landing />} />
      <Route path="/es" element={<Landing />} />
      <Route path="/dashboard" element={<Protected><Dashboard /></Protected>} />
      <Route path="/programs" element={<Protected><Programs /></Protected>} />
      <Route path="/programs/:id" element={<Protected><ProgramDetail /></Protected>} />
      <Route path="/agencies" element={<Protected><Agencies /></Protected>} />
      <Route path="/contacts" element={<Protected><Contacts /></Protected>} />
      <Route path="/properties" element={<Protected><Properties /></Protected>} />
      <Route path="/properties/:id" element={<Protected><PropertyDetail /></Protected>} />
      <Route path="/match" element={<Protected><MatchingEngine /></Protected>} />
      <Route path="/underwriting" element={<Protected><Underwriting /></Protected>} />
      <Route path="/scenarios" element={<Protected><Scenarios /></Protected>} />
      <Route path="/access-requests" element={<Protected><AccessRequests /></Protected>} />
      <Route path="/leads" element={<Protected><Leads /></Protected>} />
      <Route path="/leads/:id" element={<Protected><LeadProfile /></Protected>} />
      <Route path="/find" element={<Seeker />} />
      <Route path="/es/find" element={<Seeker />} />
      <Route path="/provider" element={<Provider />} />
      <Route path="/es/provider" element={<Provider />} />
      <Route path="/privacy" element={<Privacy />} />
      <Route path="/es/privacy" element={<Privacy />} />
      <Route path="/terms" element={<Terms />} />
      <Route path="/es/terms" element={<Terms />} />
      <Route path="/accessibility" element={<Accessibility />} />
      <Route path="/es/accessibility" element={<Accessibility />} />
      <Route path="/place/:key" element={<Protected><Place /></Protected>} />
      <Route path="/places/:key" element={<Protected><Place /></Protected>} />
      <Route path="/admin/data-inventory" element={<Protected><AdminDataInventory /></Protected>} />
      <Route path="/admin/alternative-uses" element={<Protected><AdminAlternativeUses /></Protected>} />
      <Route path="/packets" element={<Protected><Packets /></Protected>} />
      <Route path="/packets/:id" element={<Protected><PacketDetail /></Protected>} />
      <Route path="/packets/:id/preview" element={<Protected><PacketPreview /></Protected>} />
      <Route path="/funding" element={<Protected><Funding /></Protected>} />
    </Routes>
  );
}

export default function App() {
  useEffect(() => { document.title = "Throughline · Housing Opportunity Infrastructure"; }, []);
  return (
    <AuthProvider>
      <BrowserRouter>
        <AppRouter />
        <Toaster position="top-right" richColors />
      </BrowserRouter>
    </AuthProvider>
  );
}
